import type { jsPDF } from "jspdf";
import {
  applyTextCase,
  effectiveThemeForSlide,
  hexToRgb,
  normRectToPdfMm,
  pdfFontSizePtForSlideHeight,
  pdfSlideHeightPx,
  resolveFittedTextStyle,
  isTextRasterLayer,
  getTextRasterAssetKey,
  getRasterBackdrop,
  getTextBoxBackground,
  layerRenderStyle,
  sortedLayers,
  stripSpeakerScriptPlain,
  surfPreviewScrimStyle,
  SURF_PDF_PAGE_HEIGHT_MM,
  SURF_PDF_PAGE_SIZE,
  SURF_PDF_PAGE_WIDTH_MM,
  chromaKeyHexForLayer,
  getLayerCrop,
  isComposedBackgroundMode,
  needsRasterChromaKeyPrep,
  usesRasterSolidPanel,
  type SurfDeck,
  type SurfImageCrop,
  type SurfLayer,
  type SurfSlide,
  type SurfStyle,
  type TextLayoutContext,
} from "@vida/surf-core";
import { ensureSurfPdfFonts, pdfFontForResolved } from "./surfPdfFonts.js";
import { drawTableLayer } from "./surfPdfTable.js";
import { drawChartLayer } from "./surfPdfChart.js";
import { drawGraphicLayer } from "./surfPdfGraphic.js";
import {
  prefetchImages,
  layerPayloadsFromUrls,
  collectExportImageUrls,
  applyLayerCropsToPayloads,
  applyLayerCoversToPayloads,
  imageAspectFromPayload,
  type ImagePayload,
} from "./imagePayload.js";
import {
  createWebTextLayoutContext,
  ensureSurfFontsLoaded,
  layoutLayerTextLines,
} from "../utils/surfTextMeasure.js";

export type SurfPdfExportInput = {
  deck: SurfDeck;
  /** slideId → presigned image URL */
  slideBackgroundUrls?: Record<string, string>;
  /** layerId → presigned image URL (content widgets) */
  layerImageUrls?: Record<string, string>;
  /** Append speaker script as notes page after each slide */
  includeSpeakerNotes?: boolean;
};

function drawTextLayer(
  pdf: jsPDF,
  layer: SurfLayer,
  theme: SurfStyle,
  rect: ReturnType<typeof normRectToPdfMm>,
  layout: TextLayoutContext,
  slideHeightMm: number = SURF_PDF_PAGE_HEIGHT_MM,
): void {
  const ts = resolveFittedTextStyle(layer, theme, layout);
  const render = layerRenderStyle(layer, theme, layout.slideHeightPx);
  const fontSpec = pdfFontForResolved(ts.font, ts.weight);
  pdf.setFont(fontSpec.name, fontSpec.style);
  pdf.setFontSize(pdfFontSizePtForSlideHeight(ts.sizeRel, slideHeightMm));
  const [r, g, b] = hexToRgb(render.fillText);
  pdf.setTextColor(r, g, b);

  const text = applyTextCase(layer.content?.trim() || "", ts.case);
  if (!text) return;

  const fontSizePx = ts.sizeRel * layout.slideHeightPx;
  const lines = layoutLayerTextLines(
    text,
    layer.w,
    ts.font,
    ts.weight,
    ts.sizeRel,
    ts.lineHeight,
    layout.slideHeightPx,
    ts.maxLines,
    layout.measurer,
  );

  // Map layout px → mm in the target slide rectangle (full page or scaled thumbnail).
  const mmPerPx = slideHeightMm / layout.slideHeightPx;
  const fontSizeMm = fontSizePx * mmPerPx;
  const lineHeightMm = fontSizeMm * ts.lineHeight;
  const halfLeadingMm = (lineHeightMm - fontSizeMm) / 2;
  let y = rect.y + halfLeadingMm;
  const maxY = rect.y + rect.h;

  for (const line of lines) {
    if (y > maxY) break;
    const lineWidthMm =
      layout.measurer(line, ts.font, ts.weight, fontSizePx) * mmPerPx;
    let x = rect.x;
    if (ts.align === "center") x = rect.x + (rect.w - lineWidthMm) / 2;
    else if (ts.align === "right") x = rect.x + rect.w - lineWidthMm;
    pdf.text(line, x, y, {
      align: "left",
      angle: layer.rotation,
      baseline: "top",
    });
    y += lineHeightMm;
  }
}

function drawSolidRect(
  pdf: jsPDF,
  rect: ReturnType<typeof normRectToPdfMm>,
  hex: string,
): void {
  const [r, g, b] = hexToRgb(hex);
  pdf.setFillColor(r, g, b);
  pdf.rect(rect.x, rect.y, rect.w, rect.h, "F");
}

function needsRasterBackdropPanel(layer: SurfLayer, theme: SurfStyle): string | null {
  if (!usesRasterSolidPanel(layer)) return null;
  if (isTextRasterLayer(layer) && getTextRasterAssetKey(layer)) {
    return getRasterBackdrop(layer, theme);
  }
  if (layer.type === "image" && getLayerTextRender(layer) === "in_image" && layer.content?.trim()) {
    return getRasterBackdrop(layer, theme);
  }
  return null;
}

function getLayerTextRender(layer: SurfLayer): "overlay" | "in_image" {
  const raw = layer.style?.textRender;
  return raw === "in_image" ? "in_image" : "overlay";
}

function drawShapeLayer(
  pdf: jsPDF,
  layer: SurfLayer,
  theme: SurfStyle,
  rect: ReturnType<typeof normRectToPdfMm>,
): void {
  const [r, g, b] = hexToRgb(theme.palette.accent);
  pdf.setFillColor(r, g, b);
  pdf.roundedRect(rect.x, rect.y, rect.w, rect.h, 2, 2, "F");
  void layer.rotation;
}

export type SurfExportImageFit = "cover" | "contain" | "fill";

/**
 * Fit immagine per l'export, allineato al render canvas (EditorLayer):
 *  - crop presente  → "fill" (la regione crop è già bakata nel payload → riempie il box)
 *  - text raster    → "contain" (CSS .editor-layer-text-raster-img)
 *  - ogni altra img (overlay + in_image) → "cover" (objectFit: cover del canvas)
 */
export function surfExportImageFit(layer: SurfLayer): SurfExportImageFit {
  if (getLayerCrop(layer)) return "fill";
  if (isTextRasterLayer(layer)) return "contain";
  return "cover";
}

/** Contain the image within the widget rect preserving aspect (matches canvas object-fit: contain). */
function containRect(
  rect: ReturnType<typeof normRectToPdfMm>,
  aspect: number,
): ReturnType<typeof normRectToPdfMm> {
  const boxAspect = rect.w / rect.h;
  if (aspect > boxAspect) {
    const h = rect.w / aspect;
    return { x: rect.x, y: rect.y + (rect.h - h) / 2, w: rect.w, h };
  }
  const w = rect.h * aspect;
  return { x: rect.x + (rect.w - w) / 2, y: rect.y, w, h: rect.h };
}

/**
 * Cover the rect preserving aspect, centered (matches CSS background-size: cover
 * / object-fit: cover). Overflow must be cropped by an active clip on `rect`.
 */
function coverRect(
  rect: ReturnType<typeof normRectToPdfMm>,
  aspect: number,
): ReturnType<typeof normRectToPdfMm> {
  const boxAspect = rect.w / rect.h;
  if (aspect > boxAspect) {
    const w = rect.h * aspect;
    return { x: rect.x - (w - rect.w) / 2, y: rect.y, w, h: rect.h };
  }
  const h = rect.w / aspect;
  return { x: rect.x, y: rect.y - (h - rect.h) / 2, w: rect.w, h };
}

/** Natural aspect of an already-decoded payload, falling back to the rect aspect. */
function resolveCoverAspect(
  pdf: jsPDF,
  payload: ImagePayload,
  rect: ReturnType<typeof normRectToPdfMm>,
): number {
  try {
    const props = pdf.getImageProperties(payload.dataUrl) as { width?: number; height?: number };
    if (props?.width && props?.height) return props.width / props.height;
  } catch {
    /* fall through to box aspect */
  }
  return rect.w / rect.h;
}

function resolveContainAspect(
  pdf: jsPDF,
  payload: ImagePayload,
  rect: ReturnType<typeof normRectToPdfMm>,
  aspectHint?: number,
): number {
  if (typeof aspectHint === "number" && Number.isFinite(aspectHint) && aspectHint > 0) {
    return aspectHint;
  }
  try {
    const props = pdf.getImageProperties(payload.dataUrl) as { width?: number; height?: number };
    if (props?.width && props?.height) return props.width / props.height;
  } catch {
    /* fall through to box aspect */
  }
  return rect.w / rect.h;
}

function drawImageLayer(
  pdf: jsPDF,
  layer: SurfLayer,
  rect: ReturnType<typeof normRectToPdfMm>,
  payload: ImagePayload | null,
  aspectHint?: number,
): void {
  if (!payload) {
    drawImagePlaceholder(pdf, layer, rect);
    return;
  }
  const fmt = payload.format === "png" ? "PNG" : "JPEG";
  const fit = surfExportImageFit(layer);
  // "contain" letterboxes the whole image inside the box preserving aspect.
  if (fit === "contain") {
    const aspect = resolveContainAspect(pdf, payload, rect, aspectHint);
    const t = containRect(rect, aspect);
    pdf.addImage(payload.dataUrl, fmt, t.x, t.y, t.w, t.h);
    return;
  }
  // "fill" (crop baked) and "cover" (center-crop baked) fill the box full-rect.
  // No clip(): the cover region is pre-baked to the box aspect, so drawing it
  // full-rect matches canvas object-fit: cover with no spurious border.
  pdf.addImage(payload.dataUrl, fmt, rect.x, rect.y, rect.w, rect.h);
}

function drawImagePlaceholder(
  pdf: jsPDF,
  layer: SurfLayer,
  rect: ReturnType<typeof normRectToPdfMm>,
): void {
  pdf.setDrawColor(100, 116, 139);
  pdf.setLineWidth(0.3);
  pdf.rect(rect.x, rect.y, rect.w, rect.h, "S");
  pdf.setFontSize(10);
  pdf.setTextColor(100, 116, 139);
  pdf.text(layer.content || "Image", rect.x + 2, rect.y + rect.h / 2);
}

export type SurfPdfRectMm = { x: number; y: number; w: number; h: number };

export type SurfPdfDrawContext = {
  layout: TextLayoutContext;
  layerPayloads: Map<string, ImagePayload>;
  imageCache: Map<string, ImagePayload>;
  /** layerId → natural width/height for contain-fit (text raster). */
  containAspects: Map<string, number>;
};

function scalePdfRect(
  rect: ReturnType<typeof normRectToPdfMm>,
  target: SurfPdfRectMm,
): ReturnType<typeof normRectToPdfMm> {
  const sx = target.w / SURF_PDF_PAGE_WIDTH_MM;
  const sy = target.h / SURF_PDF_PAGE_HEIGHT_MM;
  return {
    x: target.x + rect.x * sx,
    y: target.y + rect.y * sy,
    w: rect.w * sx,
    h: rect.h * sy,
  };
}

/** Draw a full slide composite into an arbitrary PDF rectangle (mm). */
export function drawSlideInRect(
  pdf: jsPDF,
  target: SurfPdfRectMm,
  slide: SurfSlide,
  theme: SurfStyle,
  bg: ImagePayload | null,
  layerPayloads: Map<string, ImagePayload>,
  layout: TextLayoutContext,
  containAspects: Map<string, number> = new Map(),
): void {
  const slideTheme = effectiveThemeForSlide(theme, slide);

  pdf.saveGraphicsState();
  pdf.rect(target.x, target.y, target.w, target.h);
  pdf.clip();
  // discardPath è obbligatorio: senza, jsPDF emette `re S W` (path svuotato dallo
  // stroke) e il clip `W` si aggancia al PROSSIMO path disegnato (es. il bordo del
  // placeholder immagine), clippando erroneamente tutti i layer a quel rettangolo.
  pdf.discardPath();

  if (bg) {
    const fmt = bg.format === "png" ? "PNG" : "JPEG";
    // Canvas renders the background with `background-size: cover` (crop to fill,
    // centered). Match it: draw a cover rect and let the slide clip crop overflow.
    // Drawing full-rect (stretch) would shift baked decorations (e.g. panel frames)
    // vertically when the generated background is not exactly 16:9.
    const c = coverRect(target, resolveCoverAspect(pdf, bg, target));
    pdf.addImage(bg.dataUrl, fmt, c.x, c.y, c.w, c.h);
  } else {
    const manual = slide.background.solidColor?.trim();
    const fillHex =
      manual && /^#[0-9A-Fa-f]{6}$/i.test(manual) ? manual : theme.palette.surface;
    const [r, g, b] = hexToRgb(fillHex);
    pdf.setFillColor(r, g, b);
    pdf.rect(target.x, target.y, target.w, target.h, "F");
  }

  if (!isComposedBackgroundMode(slideTheme)) {
    const scrimCss = surfPreviewScrimStyle(slideTheme);
    if (scrimCss) {
      const leg = slideTheme.legibility;
      if (leg.strategy === "scrim" || leg.strategy === "box") {
        const scrim = leg.scrim ?? { color: "#000000", opacity: 0.35 };
        const [sr, sg, sb] = hexToRgb(scrim.color);
        const opacity = typeof scrimCss.opacity === "number" ? scrimCss.opacity : scrim.opacity;
        pdf.saveGraphicsState();
        pdf.setGState(pdf.GState({ opacity }));
        pdf.setFillColor(sr, sg, sb);
        pdf.rect(target.x, target.y, target.w, target.h, "F");
        pdf.restoreGraphicsState();
      }
    }
  }

  for (const layer of sortedLayers(slide)) {
    const rect = scalePdfRect(normRectToPdfMm(layer), target);
    const panel = needsRasterBackdropPanel(layer, slideTheme);
    if (panel) drawSolidRect(pdf, rect, panel);
    if (layer.type === "text") {
      if (isTextRasterLayer(layer) && getTextRasterAssetKey(layer)) {
        drawImageLayer(
          pdf,
          layer,
          rect,
          layerPayloads.get(layer.id) ?? null,
          containAspects.get(layer.id),
        );
      } else {
        const boxBg = getTextBoxBackground(layer);
        if (boxBg) {
          const [br, bgc, bb] = hexToRgb(boxBg.color);
          pdf.saveGraphicsState();
          pdf.setGState(pdf.GState({ opacity: boxBg.opacity }));
          pdf.setFillColor(br, bgc, bb);
          pdf.roundedRect(rect.x, rect.y, rect.w, rect.h, 1.5, 1.5, "F");
          pdf.restoreGraphicsState();
        }
        drawTextLayer(pdf, layer, slideTheme, rect, layout, target.h);
      }
    } else if (layer.type === "shape") drawShapeLayer(pdf, layer, slideTheme, rect);
    else if (layer.type === "image") {
      drawImageLayer(
        pdf,
        layer,
        rect,
        layerPayloads.get(layer.id) ?? null,
        containAspects.get(layer.id),
      );
    }
    else if (layer.type === "table") drawTableLayer(pdf, layer, slideTheme, rect);
    else if (layer.type === "chart") drawChartLayer(pdf, layer, slideTheme, rect);
    else if (layer.type === "graphic") drawGraphicLayer(pdf, layer, slideTheme, rect);
  }

  pdf.restoreGraphicsState();
  pdf.setDrawColor(148, 163, 184);
  pdf.setLineWidth(0.2);
  pdf.rect(target.x, target.y, target.w, target.h, "S");
}

async function drawSlidePage(
  pdf: jsPDF,
  slide: SurfSlide,
  theme: SurfStyle,
  bg: ImagePayload | null,
  layerPayloads: Map<string, ImagePayload>,
  isFirst: boolean,
  layout: TextLayoutContext,
  containAspects: Map<string, number>,
): Promise<void> {
  if (!isFirst) pdf.addPage(SURF_PDF_PAGE_SIZE, "landscape");
  drawSlideInRect(
    pdf,
    { x: 0, y: 0, w: SURF_PDF_PAGE_WIDTH_MM, h: SURF_PDF_PAGE_HEIGHT_MM },
    slide,
    theme,
    bg,
    layerPayloads,
    layout,
    containAspects,
  );
}

export async function prepareSurfPdfDrawContext(input: SurfPdfExportInput): Promise<SurfPdfDrawContext> {
  const { deck, slideBackgroundUrls = {}, layerImageUrls = {} } = input;
  const allUrls = collectExportImageUrls(slideBackgroundUrls, layerImageUrls);
  const chromaKeyByLayerId = collectChromaKeyByLayerId(deck);
  const imageCache = await prefetchImages(allUrls, {
    chromaKeyByLayerId,
    layerUrls: layerImageUrls,
  });
  const layerPayloads = layerPayloadsFromUrls(layerImageUrls, imageCache);
  await applyLayerCropsToPayloads(collectLayerCrops(deck), layerPayloads);
  await applyLayerCoversToPayloads(collectLayerCovers(deck), layerPayloads);
  const containAspects = await collectContainAspects(deck, layerPayloads);
  const fontSpecs = collectDeckFontSpecs(deck);
  await ensureSurfFontsLoaded(fontSpecs);
  const layout = createWebTextLayoutContext(pdfSlideHeightPx());
  return { layout, layerPayloads, imageCache, containAspects };
}

export function slideBackgroundPayload(
  slideId: string,
  slideBackgroundUrls: Record<string, string>,
  imageCache: Map<string, ImagePayload>,
): ImagePayload | null {
  const url = slideBackgroundUrls[slideId];
  if (!url) return null;
  return imageCache.get(url) ?? null;
}

export function triggerSurfPdfDownload(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Client-side PDF export — vector text + full-bleed background bitmap.
 * Fallback server-side (Puppeteer) non implementato in MVP; usare export client.
 */
export async function exportSurfDeckToPdf(
  input: SurfPdfExportInput,
  filename?: string,
): Promise<Blob> {
  const { jsPDF } = await import("jspdf");
  const { deck, slideBackgroundUrls = {}, includeSpeakerNotes = false } = input;
  if (!deck.slides.length) throw new Error("Deck senza slide.");

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: SURF_PDF_PAGE_SIZE,
  });

  await ensureSurfPdfFonts(pdf);

  const { layout, layerPayloads, imageCache, containAspects } = await prepareSurfPdfDrawContext(input);

  for (let i = 0; i < deck.slides.length; i += 1) {
    const slide = deck.slides[i]!;
    const bg = slideBackgroundPayload(slide.id, slideBackgroundUrls, imageCache);
    await drawSlidePage(pdf, slide, deck.theme, bg, layerPayloads, i === 0, layout, containAspects);
    const notes = stripSpeakerScriptPlain(slide.speakerScript?.text ?? "");
    if (includeSpeakerNotes && notes) {
      pdf.addPage(SURF_PDF_PAGE_SIZE, "landscape");
      pdf.setFontSize(11);
      pdf.setTextColor(40, 40, 40);
      const lines = pdf.splitTextToSize(notes, SURF_PDF_PAGE_WIDTH_MM - 24);
      pdf.text(lines, 12, 18);
    }
  }

  const safeName = (filename ?? `${deck.title || "presentazione"}.pdf`).replace(/[^\w.\- ]+/g, "_");
  const blob = pdf.output("blob");
  triggerSurfPdfDownload(blob, safeName.endsWith(".pdf") ? safeName : `${safeName}.pdf`);
  return blob;
}

function collectDeckFontSpecs(deck: SurfDeck): Array<{ font: string; weight: number }> {
  const specs: Array<{ font: string; weight: number }> = [];
  for (const slide of deck.slides) {
    for (const layer of slide.layers) {
      if (layer.type !== "text") continue;
      const ts = resolveFittedTextStyle(layer, deck.theme);
      specs.push({ font: ts.font, weight: ts.weight });
    }
  }
  return specs;
}

export function collectLayerCrops(
  deck: SurfDeck,
): Array<{ layerId: string; crop: SurfImageCrop }> {
  const out: Array<{ layerId: string; crop: SurfImageCrop }> = [];
  for (const slide of deck.slides) {
    for (const layer of slide.layers) {
      // Crop applies to image widgets AND raster artifacts on text layers.
      // Both disable export "contain" when cropped, so the crop MUST be baked
      // into the payload or the full image gets stretched to the widget box.
      const isImage = layer.type === "image";
      const isRasterText = isTextRasterLayer(layer) && Boolean(getTextRasterAssetKey(layer));
      if (!isImage && !isRasterText) continue;
      const crop = getLayerCrop(layer);
      if (crop) out.push({ layerId: layer.id, crop });
    }
  }
  return out;
}

/** Page aspect of the export canvas (16:9 for both PDF and PPTX). */
const SURF_EXPORT_PAGE_ASPECT = SURF_PDF_PAGE_WIDTH_MM / SURF_PDF_PAGE_HEIGHT_MM;

/**
 * Collect the box aspect for every layer whose export fit is "cover", so the
 * payload can be center-cropped to that aspect (baked cover, no clip in PDF/PPTX).
 * Box aspect = (layer.w / layer.h) * pageAspect, since layer w/h are normalized 0..1.
 */
/** Natural image aspects for layers whose export fit is contain (text raster). */
export async function collectContainAspects(
  deck: SurfDeck,
  layerPayloads: Map<string, ImagePayload>,
): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  const tasks: Array<Promise<void>> = [];
  for (const slide of deck.slides) {
    for (const layer of slide.layers) {
      if (surfExportImageFit(layer) !== "contain") continue;
      const payload = layerPayloads.get(layer.id);
      if (!payload) continue;
      tasks.push(
        (async () => {
          const aspect = await imageAspectFromPayload(payload);
          if (aspect != null && Number.isFinite(aspect) && aspect > 0) {
            out.set(layer.id, aspect);
          }
        })(),
      );
    }
  }
  await Promise.all(tasks);
  return out;
}

export function collectLayerCovers(
  deck: SurfDeck,
): Array<{ layerId: string; boxAspect: number }> {
  const out: Array<{ layerId: string; boxAspect: number }> = [];
  for (const slide of deck.slides) {
    for (const layer of slide.layers) {
      if (surfExportImageFit(layer) !== "cover") continue; // crop→fill, raster→contain
      if (!(layer.h > 0)) continue;
      out.push({
        layerId: layer.id,
        boxAspect: (layer.w / layer.h) * SURF_EXPORT_PAGE_ASPECT,
      });
    }
  }
  return out;
}

function collectChromaKeyByLayerId(deck: SurfDeck): Record<string, string> {
  const out: Record<string, string> = {};
  for (const slide of deck.slides) {
    for (const layer of slide.layers) {
      if (needsRasterChromaKeyPrep(layer)) {
        out[layer.id] = chromaKeyHexForLayer(layer, deck.theme);
      }
    }
  }
  return out;
}
